import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from "react-router-dom";
import WebViewer from '@pdftron/webviewer'
import { useDispatch, useSelector } from "react-redux";
import { editPDFsLock, autoSavePDF } from "../../slice/PDFEditSlice";
import { DocumentContentHeaderWrapper, SpanBold, SpanName, ViewMSFileButton, ViewMSWrapper, WarningLabel } from '../TabView/TabViewStyled';
import MoreButton from '../MoreButton/MoreButton';
import {
  buttonStyles,
  secondaryButtonStyles,
} from "../MoreButton/MoreButtonStyled";
import Delete from '../../assets/images/Delete.svg';
import Rename from '../../assets/images/Rename.svg';
import Merge from '../../assets/images/merge.svg'
import Approve from '../../assets/images/Approve.svg';
import Rework from '../../assets/images/Rework.svg';
import FileProperties from "../../assets/images/File properties icon.svg"
import ReviewNotes from "../../assets/images/review notes.svg";
import ESignImage from "../../assets/images/E-sign.png";
import DownloadIcon from "../../assets/images/download.svg";
import Breadcrumb from '../BreadCrumbComponent/BreadCrumb';
import { fetchBreadCrumbs, getDocumentDetails, setExpandedKeys, setUpdateMeta, updateDocumentDetail } from '../../slice/MatterDetailsSlice';
import PropTypes from 'prop-types';
import Loader from '../Loader/Loader';
import ErrorToast from '../Notification/ErrorToast';
import { convertData } from '../../utils/RestructureToDateTree';
import debounce from 'lodash.debounce';
import { Button } from '@mui/material';
import ExternalShare from '../../assets/images/externalShare.svg';
import SendIcon from '../../assets/images/send.svg';
import { DISABLE_AUTOSAVE_PLACEHOLDERS, MS_FILE_TYPES, PDF_FILE_TYPES } from '../../utils/constants';
import { getExpandedKeys } from '../../utils/getExpandedKeys';
import {
  configurePermissions,
  errorHandler,
  getEmailPreviewUrl,
  getApryseExtension,
  getFileType,
  setupBreadcrumbTrail,
  getDocumentStreamingUrl,
  downloadDocumentFile,
} from './DocumentUtils';
import { sendWebSocketMessage } from '../../middleware/websocketMiddleware';
import { clearAnnotationUpdate } from '../../slice/WebsocketSlice';
import { getCachedDocument, cacheDocument, invalidateDocument } from '../../service/documentCache';
import { getDocumentMarkdown } from '../../service/analysis';
import { findPagesForQuery, splitMarkdownIntoPages } from '../../utils/ocrSearchFallback';
import { highlightHiddenText } from '../../utils/viewerWordSearch';
import OcrSearchFallbackBanner from './OcrSearchFallbackBanner';
import AnalysisIcon from '../../assets/images/sparkle.svg';
// Import AnalyzeButton directly (not via index) so we don't pull in the
// full AIPane bundle (ChatPane, BriefingTab, AnalysisTab) into the
// document viewer's module graph.
import AnalyzeButton from '../AIPane/AnalyzeButton';
import CreateWorkItemModal from '../Inbox/CreateWorkItemModal';
import { useNavigate } from 'react-router-dom';
import { buildAIPaneSearch } from '../../utils/aiPaneUrl';
import { selectIsAnalysisRunningForDocument } from '../../slice/AISlice';
import { absolutizeSharePointUrl } from '../TabView/sharePointOffice';
const Document = (props) => {
  const dispatch = useDispatch();
  // eslint-disable-next-line react/prop-types
  const {
    documentRecord,
    isLocked,
    setRenameEditFlag,
    setFileProp,
    setOpen,
    loading,
    handleBreadcrumbClick,
    setActiveTab,
    rename,
    setShowShareModal,
    setShowReviewActionModal,
    setShowDeleteModal,
    setSelectedDoc,
    setOpenReviewNotes,
    setOpenESignModal,
    setShareId,
    openESignModal,
    setShowExternalShareModal,
    setShowSendMessageModal,
    setDocLoading,
    setSelectedFile,
    setIsModalOpen,
    isActive = true,
  } = props;
  const { matterDetailsTree, documentDetail } = useSelector((state) => state.matterDetails);
  const annotationUpdate = useSelector((state) => state.websocket.annotationUpdate);
  const treeData = useMemo(() => convertData([matterDetailsTree]), [matterDetailsTree]);
  const [isLoading, setisLoading] = useState(loading);
  const [showNotification, setShowNotification] = useState(false);
  const [errorMsg, setErrorMsg] = useState([])
  const [loadingStatus, setLoadingStatus] = useState("idle")
  const [shareContext, setShareContext] = useState({})
  const viewer = useRef(null);
  const instanceRef = useRef(null);
  const isInitialized = useRef(false);
  const lockedDocsRef = useRef(new Set());
  const activePathRef = useRef(null);
  const isApplyingRemoteUpdate = useRef(false);
  const isMountedRef = useRef(true);
  const initializationPromiseRef = useRef(null);
  const documentLoadPromiseRef = useRef(null);
  const hasUserChangesRef = useRef(false); // Track if user has made actual changes
  const documentUnloadedCleanupRef = useRef(null); // Store cleanup function for documentUnloaded listener

  // Viewer search fallback for scanned pages (see utils/ocrSearchFallback).
  // The viewer's search reads only the PDF's embedded text layer; when it
  // finds nothing, the document's OCR text is searched instead and the
  // reader is offered the pages that hold the words. The OCR pages are
  // fetched once per document and kept here; the banner state drives the
  // overlay rendered next to the viewer.
  const ocrPagesRef = useRef({ documentId: null, pages: null, promise: null });
  const [ocrSearchFallback, setOcrSearchFallback] = useState(null);
  const ocrSearchListenerRef = useRef(null);

  // eslint-disable-next-line no-unused-vars
  const [autoSave, setAutoSave] = useState(null);
  const [workItemModalOpen, setWorkItemModalOpen] = useState(false);
  let intervalId = null;
  // eslint-disable-next-line no-unused-vars
  const [documentBreadcrumb, setDocumentBreadcrumb] = useState([]);
  const { expandedKeys: prevExpandedKeys } = useSelector((state) => state.matterDetails);
  const { showActions } = useSelector((state) => state.editPDF);

  const workflowActions = shareContext?.workflow_actions || [];
  const showSubmitButton = workflowActions.length === 1 && workflowActions[0] === "submit";
  const showActionButton =
    workflowActions.includes("approve_task")
    || workflowActions.includes("approve_forward")
    || workflowActions.includes("request_rework");
  const submitDisabled = !shareContext?.submit_enabled;
  const actionDisabled = !shareContext?.action_enabled;

  const fileType = getFileType(documentRecord.file_type);
  const apryseExtension = getApryseExtension(
    documentRecord.name || documentRecord.file_type
  );
  // The email-preview endpoint renders a complete PDF response on demand, so
  // byte-range loading would repeat conversion work. Existing document types
  // retain their streaming behavior.
  const shouldUseStreaming = fileType !== 'email';

  let firstLoad = false;
  const location = useLocation();
  const navigate = useNavigate();
  const urlIds = location.pathname.split("/matter/")[1].split("/");
  const documentIdForAI = documentRecord?.unique_id
    ? documentRecord.unique_id.substring(1)
    : null;
  const isAnalysisRunningForDoc = useSelector((state) =>
    selectIsAnalysisRunningForDocument(state, documentIdForAI)
  );

  const openAIAnalysisTab = () => {
    if (!documentIdForAI) return;
    const search = buildAIPaneSearch(location.search, "analysis");
    navigate(`${location.pathname}${search}`, { replace: false });
  };
  
  // Store highlight metadata in a ref so it persists across renders
  const highlightMetadataRef = useRef(null);
  
  // Capture highlight metadata from location.state when it's available
  useEffect(() => {
    console.log('🔍 Location state changed:', location.state);
    console.log('🔍 Location pathname:', location.pathname);
    
    // Try to get from location.state first
    if (location.state?.highlightMetadata) {
      highlightMetadataRef.current = location.state.highlightMetadata;
      console.log('📌 Highlight metadata captured from location.state:', highlightMetadataRef.current);
      // Clear sessionStorage after using it
      try {
        sessionStorage.removeItem('chatHighlightMetadata');
      } catch (e) {
        console.error('Failed to clear sessionStorage:', e);
      }
    } else {
      // Fallback to sessionStorage if location.state doesn't have it
      try {
        const stored = sessionStorage.getItem('chatHighlightMetadata');
        if (stored) {
          highlightMetadataRef.current = JSON.parse(stored);
          console.log('📌 Highlight metadata captured from sessionStorage:', highlightMetadataRef.current);
          sessionStorage.removeItem('chatHighlightMetadata');
        } else {
          console.log('⚠️ No highlight metadata in location.state or sessionStorage');
        }
      } catch (e) {
        console.error('Failed to read from sessionStorage:', e);
      }
    }
  }, [location.state, location.pathname]);

  useEffect(() => {
    dispatch(getDocumentDetails(documentRecord.unique_id.substring(1)))
if(!documentDetail?.locked_by && localStorage.getItem("lastclosed")!==documentDetail.unique_id){
  console.log("here is locked",documentDetail?.locked_by)
  // Only call setDocumentChanged for PDF files (autosave is PDF-only)
  if (fileType === 'pdf') {
  setDocumentChanged(true);
  }
  configureDocumentPermissions(true, fileType)
}
    return(() => {
      setShareId("")
    })
 },[documentRecord.unique_id, documentDetail.locked_by])

  // Set shareContext from documentDetail.sharing_context (replaces sharingContext API call)
  useEffect(() => {
    if (documentDetail?.sharing_context) {
      setShareContext(documentDetail.sharing_context);
      setShareId(documentDetail.sharing_context?.share_ids?.[0] || "");
    } else {
      setShareContext({});
      setShareId("");
    }
  }, [documentDetail?.sharing_context]);

  const handleAutoSave = async (documentViewer, annotationManager, status) => {
    // Autosave is only for PDF files, not MS Office files
    if (fileType !== 'pdf') {
      return;
    }

    // Only autosave if there are actual user changes
    if (!hasUserChangesRef.current) {
      return;
    }

    const doc = documentViewer?.getDocument();

    if (!doc) return;

    try {
      const xfdfString = await annotationManager.exportAnnotations();
      const data = await doc.getFileData({ xfdfString, doctype: 'pdf' });
      const fileBlob = new Blob([data], { type: 'application/pdf' });
      if (!openESignModal && status && firstLoad === true) {
        setAutoSave("Autosaving...");
        await dispatch(
          autoSavePDF({ id: documentRecord.unique_id.substring(1), file: fileBlob })
        ).unwrap().then((res) => {
          setAutoSave(`Saved to ${documentRecord.name}`);
          dispatch(setUpdateMeta(res?.data?.document));
          // Reset the change flag after successful save
          hasUserChangesRef.current = false;
          // Update IndexedDB cache with the freshly-saved blob
          const newVersion = res?.data?.document?.updated_at || res?.data?.document?.version;
          if (newVersion) {
            cacheDocument(documentRecord.unique_id.substring(1), fileBlob, newVersion)
              .catch(err => console.warn('Failed to update cache after auto-save:', err));
          }
        }).catch((err) => {
          errorHandler(err?.data?.error || err?.data?.detail || "Failed to Save the File.", setErrorMsg, setShowNotification);
          setisLoading(false);
        })
        setTimeout(() => {
          setAutoSave(null);
        }, 5000);
      }
      firstLoad = true;
    } catch (error) {
      console.error("Auto-save failed:", error);
    }
  };

  const startInterval = (documentViewer, annotationManager, status) => {
    handleAutoSave(documentViewer, annotationManager, status);
  };

  const stopInterval = () => {
    if (intervalId) {
      clearInterval(intervalId);
      setAutoSave(null)
      intervalId = null;
      setAutoSave(null)

    }
  };

  // Helper function to properly cleanup WebViewer instance
  const cleanupWebViewerInstance = async (viewerInstance, logPrefix = '') => {
    if (!viewerInstance) return;
    
    try {
      const documentViewer = viewerInstance.Core?.documentViewer;
      const ui = viewerInstance.UI;
      const core = viewerInstance.Core;
      
      // FIRST: Try to cancel all ongoing downloads/network requests via managers
      try {
        // Try netManager
        const netManager = core?.netManager;
        if (netManager) {
          if (typeof netManager.cancelAllDownloads === 'function') {
            console.log(`${logPrefix}🧹 Cancelling all downloads via netManager...`);
            netManager.cancelAllDownloads();
            console.log(`${logPrefix}✅ netManager.cancelAllDownloads() called`);
          } else if (typeof netManager.cancelAll === 'function') {
            console.log(`${logPrefix}🧹 Cancelling all via netManager.cancelAll()...`);
            netManager.cancelAll();
            console.log(`${logPrefix}✅ netManager.cancelAll() called`);
          }
        }
        
        // Try downloadManager
        const downloadManager = core?.downloadManager;
        if (downloadManager) {
          if (typeof downloadManager.cancelAll === 'function') {
            console.log(`${logPrefix}🧹 Cancelling all downloads via downloadManager...`);
            downloadManager.cancelAll();
            console.log(`${logPrefix}✅ downloadManager.cancelAll() called`);
          } else if (typeof downloadManager.cancelAllDownloads === 'function') {
            console.log(`${logPrefix}🧹 Cancelling all downloads via downloadManager.cancelAllDownloads()...`);
            downloadManager.cancelAllDownloads();
            console.log(`${logPrefix}✅ downloadManager.cancelAllDownloads() called`);
          }
        }
        
        // Try to get current document and cancel its download
        if (documentViewer) {
          try {
            const doc = documentViewer.getDocument();
            if (doc && typeof doc.cancelDownload === 'function') {
              console.log(`${logPrefix}🧹 Cancelling document download...`);
              doc.cancelDownload();
              console.log(`${logPrefix}✅ Document download cancelled`);
            }
          } catch (docError) {
            // Document might not be loaded yet, ignore
          }
        }
      } catch (cancelError) {
        console.warn(`${logPrefix}Could not cancel downloads:`, cancelError);
      }
      
      // SECOND: Close document and wait for it to complete - this stops streaming requests
      if (documentViewer) {
        try {
          if (typeof documentViewer.closeDocument === 'function') {
            console.log(`${logPrefix}🧹 Closing document to stop streaming...`);
            const closePromise = documentViewer.closeDocument();
            // closeDocument may return a promise - await it if it does
            if (closePromise && typeof closePromise.then === 'function') {
              await closePromise;
              console.log(`${logPrefix}✅ Document closed and streaming stopped`);
            } else {
              console.log(`${logPrefix}✅ Document close called`);
            }
          }
        } catch (closeError) {
          console.warn(`${logPrefix}Error closing document:`, closeError);
        }
        
        // THIRD: Dispose documentViewer
        if (typeof documentViewer.dispose === 'function') {
          console.log(`${logPrefix}🧹 Disposing documentViewer...`);
          await documentViewer.dispose();
          console.log(`${logPrefix}✅ documentViewer disposed`);
        }
      }
      
      // FOURTH: Dispose the UI
      if (ui && typeof ui.dispose === 'function') {
        console.log(`${logPrefix}🧹 Disposing UI...`);
        await ui.dispose();
        console.log(`${logPrefix}✅ UI disposed`);
      }
      
      console.log(`${logPrefix}✅ WebViewer cleanup completed`);
    } catch (error) {
      console.error(`${logPrefix}❌ Error in WebViewer cleanup:`, error);
    }
  };

const setDocumentChanged = (status, documentViewer, annotationManager) => {
  // Autosave is only for PDF files, not MS Office files
  if (fileType !== 'pdf') {
    return;
  }

  if (documentDetail.locked_by !== Number(localStorage.getItem("user_id"))) {
    const urlIds = location.pathname.split("/matter/")[1].split("/");
    const lastSegment = urlIds.at(-1);
    const docId = lastSegment.substring(1);

    if (lockedDocsRef.current.has(docId)) {
      console.log(`Doc ${docId} already locked, skipping API call.`);
      return;
    }
    setisLoading(true);

    dispatch(editPDFsLock(docId))
      .unwrap()
      .then((res) => {
        console.log(`Doc ${docId} added to ref`);
        if(docId == documentDetail?.id) {
          dispatch(updateDocumentDetail({ locked_by: res?.data?.locked_by, is_locked: true }));
        }
        dispatch(getDocumentDetails(documentRecord.unique_id.substring(1)))
        lockedDocsRef.current.add(docId);
        if (status) {
          startInterval(documentViewer, annotationManager, status);
        }
      })
      .catch((err) => {
        const id = Date.now().toString();
        const newMsg =
          err?.response?.data?.detail ||
          err?.message ||
          err?.toString() ||
          "Failed to lock document";

        console.error("Error locking pdf:", err);
        setErrorMsg([{ id, newMsg }]);
        setShowNotification(true);
      })
      .finally(() => {
        setisLoading(false);
      });
  }
};

useEffect(() => {
  let pdfSetupInterval;
  let attempts = 0;
  isInitialized.current = false;
  isMountedRef.current = true;

  dispatch(getDocumentDetails(documentRecord.unique_id.substring(1))).then((res) => {
    // Check if component is still mounted before proceeding
    if (!isMountedRef.current) {
      console.log('Component unmounted, skipping document load');
      return;
    }

    if (fileType === 'unknown') {
      console.error('❌ Unsupported file type:', documentRecord.file_type);
      setShowNotification(true);
      setErrorMsg((prev) => [...prev, { 
        id: Date.now().toString(), 
        newMsg: `Unsupported file type: ${documentRecord.file_type}` 
      }]);
      setisLoading(false); // Hide loader on error
      return;
    }

    // Email source formats are converted server-side to a read-only PDF before
    // reaching WebViewer. Every other document keeps the existing stream path.
    const documentId = res?.payload?.data?.id || documentRecord.unique_id.substring(1);
    let currentPath = fileType === 'email'
      ? getEmailPreviewUrl(documentId)
      : getDocumentStreamingUrl(documentId);

    if (!currentPath) {
      console.error('❌ No path found for document ID:', documentId);
      setisLoading(false); // Hide loader on error
      return;
    }

    if(shouldUseStreaming) {
    // Add cache-busting parameter to prevent WebViewer from loading cached version
    // This ensures we always get the latest version after autosave
    try {
      // Check if URL is absolute (has protocol) or relative
      if (currentPath.startsWith('http://') || currentPath.startsWith('https://')) {
        // Absolute URL - use URL constructor
        const url = new URL(currentPath);
        // Use document ID and current timestamp to create a unique cache key
        // This will force WebViewer to fetch fresh content
        const cacheBuster = `${documentRecord.unique_id.substring(1)}_${Date.now()}`;
        url.searchParams.set('_cb', cacheBuster);
        currentPath = url.toString();
      } else {
        // Relative URL - manually append query parameter
        const separator = currentPath.includes('?') ? '&' : '?';
        const cacheBuster = `${documentRecord.unique_id.substring(1)}_${Date.now()}`;
        currentPath = `${currentPath}${separator}_cb=${encodeURIComponent(cacheBuster)}`;
      }
    } catch (error) {
      // Fallback: if URL parsing fails, append query parameter manually
      console.warn('Failed to parse URL for cache busting, using fallback:', error);
      const separator = currentPath.includes('?') ? '&' : '?';
      const cacheBuster = `${documentRecord.unique_id.substring(1)}_${Date.now()}`;
      currentPath = `${currentPath}${separator}_cb=${encodeURIComponent(cacheBuster)}`;
    }
  }

    // CRITICAL: Set loader to visible and keep it until documentLoaded/contentLoaded events fire
    // This blocks navigation/interaction during document loading to prevent streaming request issues
    //setDocLoading(true);
    //setisLoading(true);
    activePathRef.current = currentPath;
    // Reset change flag when switching documents/tabs - no autosave should trigger
    hasUserChangesRef.current = false;

    const handleOfficeDocumentLoaded = () => {
      if (instanceRef.current) {
        instanceRef.current.Core.annotationManager.enableReadOnlyMode();
        console.log("✅ MS Office file loaded in READ-ONLY mode");
      }
    };

    const setupViewer = async () => {
      if (instanceRef.current || !isMountedRef.current) return;

      try {
        console.log(`🚀 Initializing WebViewer for ${fileType} file...`);

        // Store the initialization promise so we can cancel it if needed
        const initPromise = WebViewer({
          path: '/webviewer/lib/',
          workerPath: '/webviewer/lib/core/',
          licenseKey: process.env.REACT_APP_APRYSE_LICENSE,
          fullAPI: true,
          enableWebSocket: false,
          disabledElements: ['errorModal'],
          streaming: shouldUseStreaming,     // Enable streaming mode only for files <= 70MB
          useDownloader: shouldUseStreaming, // Use downloader for streaming only when streaming is enabled
        }, viewer.current);
        console.log(`initPromise: ${initPromise}`);
        initializationPromiseRef.current = initPromise;

        const instance = await initPromise;

        // Clear the promise ref after it resolves
        if (initializationPromiseRef.current === initPromise) {
          initializationPromiseRef.current = null;
        }

        // Check again if component is still mounted after async operation
        if (!isMountedRef.current) {
          console.log('⚠️ Component unmounted during WebViewer initialization, cleaning up');
          await cleanupWebViewerInstance(instance, '⚠️ Init cleanup: ');
          return;
        }

        instanceRef.current = instance;
        configureDocumentPermissions(isLocked, fileType);
        setupDocumentListeners(instance, currentPath, fileType);
  
        // Check once more before loading document - verify component is still mounted and path hasn't changed
        if (!isMountedRef.current) {
          console.log('⚠️ Component unmounted before document load');
          return;
        }
        
        // Verify the active path hasn't changed (component might have switched documents)
        if (activePathRef.current !== currentPath) {
          console.log('⚠️ Document path changed during initialization, cancelling load');
          return;
        }

        // Cache-first loading: try IndexedDB cache before network for PDFs
        let loadSource = currentPath;
        let loadStreaming = fileType === 'email' ? false : shouldUseStreaming;
        if (fileType === 'pdf') {
          try {
            const docId = documentRecord.unique_id.substring(1);
            const cached = await getCachedDocument(docId);
            const serverVersion = res?.payload?.data?.updated_at || res?.payload?.data?.version;
            if (cached && serverVersion && cached.version === serverVersion) {
              console.log('📦 Loading PDF from IndexedDB cache');
              loadSource = cached.blob;
              loadStreaming = false;
            } else {
              if (cached) console.log('📦 Cache version mismatch, loading from network');
            }
          } catch (cacheErr) {
            console.warn('Cache lookup failed, falling back to network:', cacheErr);
          }
        }

        console.log(`🚀 Loading document ${loadStreaming ? 'with streaming' : 'without streaming'}`);
        
        // Track the document load - loadDocument returns a promise in newer versions.
        // Keep the PDF fallback for legacy renamed PDFs, but let real images and
        // Office files use their actual extension.
        const loadPromise = instance.UI.loadDocument(loadSource, {
          filename: documentRecord.name,
          streaming: loadStreaming,
          extension: apryseExtension,
          withCredentials: fileType === 'email',
        });
        
        // Store the load promise if it's actually a promise
        if (loadPromise && typeof loadPromise.then === 'function') {
          documentLoadPromiseRef.current = loadPromise;
          loadPromise.finally(() => {
            // Clear the ref when the load completes or fails (only if it's still the same promise)
            if (documentLoadPromiseRef.current === loadPromise) {
              documentLoadPromiseRef.current = null;
            }
          }).catch((error) => {
            // Log load errors only if component is still mounted
            if (isMountedRef.current) {
              console.error('Document load error:', error);
            }
          });
        }

        if (fileType === 'office') {
          instance.Core.documentViewer.addEventListener('documentLoaded', handleOfficeDocumentLoaded);
        }

        console.log(`✅ ${fileType.toUpperCase()} document load initiated`);

      } catch (err) {
        // Clear refs on error
        initializationPromiseRef.current = null;
        documentLoadPromiseRef.current = null;
        
        // Only log error if component is still mounted
        if (isMountedRef.current) {
          console.error("❌ Failed to initialize WebViewer:", err);
          setDocLoading(false);
        }
      }
    };

    const trySetup = () => {
      if (++attempts > 100) {
        clearInterval(pdfSetupInterval);
        console.error('❌ WebViewer setup timeout after 10 seconds');
        return;
      }

      if (viewer.current && !instanceRef.current) {
        clearInterval(pdfSetupInterval);
        isInitialized.current = true;
        setupViewer();
      }
    };

    if (!isInitialized.current) {
      pdfSetupInterval = setInterval(trySetup, 100);
    }

    const handleVisibilityChange = () => {
      if (document.hidden) stopInterval();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    setupBreadcrumbTrail(rename, dispatch, fetchBreadCrumbs, urlIds, setDocumentBreadcrumb);

    return () => {
      console.log('🧹 Component cleanup started - unmounting Document component');
      isMountedRef.current = false;
      clearInterval(pdfSetupInterval);
      stopInterval();
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      // Clear initialization and load promise refs (they'll be checked in the promise handlers)
      initializationPromiseRef.current = null;
      documentLoadPromiseRef.current = null;

      // IMMEDIATELY clear viewer DOM to stop any rendering that might trigger requests
      if (viewer.current) {
        viewer.current.innerHTML = '';
      }

      // Cleanup WebViewer instance - use async dispose pattern
      if (instanceRef.current) {
        const viewerInstance = instanceRef.current;
        instanceRef.current = null; // Clear ref immediately to prevent reuse
        
        // Use async IIFE to properly await dispose calls
        (async () => {
          // Remove event listener to prevent memory leaks
          if (fileType === 'office') {
            viewerInstance.Core?.documentViewer?.removeEventListener('documentLoaded', handleOfficeDocumentLoaded);
          }
          
          await cleanupWebViewerInstance(viewerInstance, '🧹 Main cleanup: ');
        })();
      } else {
        // Instance not yet initialized, but initialization might be in progress
        console.log('⚠️ WebViewer instance not initialized yet, but initialization may be in progress');
        // The instance will check isMountedRef.current when it resolves
      }

      isInitialized.current = false;
      console.log('🧹 Component cleanup finished');
    };
  });
}, [
  apryseExtension,
  documentRecord.s3_path_streaming,
  documentRecord.file_type,
  documentRecord.unique_id,
]);

  // Pause auto-save when tab is hidden, resume when active
  useEffect(() => {
    if (!isActive) {
      stopInterval();
    }
    // When becoming active again, no explicit resume needed — auto-save
    // is event-driven (annotationChanged/fieldChanged) and will fire
    // naturally when the user interacts with the now-visible tab.
  }, [isActive]);

 // Add this new useEffect to handle breadcrumb updates when rename prop changes
  useEffect(() => {
    if (rename) {
      dispatch(fetchBreadCrumbs({ item_id: urlIds.at(-1) })).then(
        (response) => {
          setisLoading(false)
          setDocumentBreadcrumb([
            { id: 0, name: "Matters" },
            ...(response?.payload?.data || []),
          ]);
        }
      );
    }
  }, [rename]);

  // Effect to clear rendering caches when documentRecord changes (tab switch)
  // This ensures refreshAll() is called even if documentUnloaded doesn't fire
  // This handles the case where Document components stay mounted but switch documents
  useEffect(() => {
    // Store the previous document ID
    const previousDocId = documentRecord?.unique_id;
    
    return () => {
      // When documentRecord changes (tab switch), clear rendering caches
      // Only do this if we have an instance and it's still valid
      if (instanceRef.current && instanceRef.current.Core?.documentViewer && isMountedRef.current) {
        const { documentViewer } = instanceRef.current.Core;
        const currentDoc = documentViewer.getDocument();
        
        // Only clear if there's a document loaded and it matches the previous document
        // (meaning we're switching away from it)
        if (currentDoc && previousDocId) {
          try {
            console.log(`🧹 Tab switch detected (doc ${previousDocId}) - clearing rendered page caches/canvases`);
            documentViewer.refreshAll();
          } catch (error) {
            console.warn('⚠️ Error calling refreshAll() on tab switch:', error);
          }
        }
      }
    };
  }, [documentRecord?.unique_id]); // Run when document changes (tab switch)

  // Separate cleanup effect that runs only on unmount to ensure WebViewer is properly disposed
  useEffect(() => {
    const viewerElement = viewer.current; // Capture ref value to avoid linter warning
    
    return () => {
      console.log('🧹 Unmount cleanup effect - disposing WebViewer instance');
      isMountedRef.current = false; // Ensure mount flag is set to false
      
      // Clear promise refs to prevent any pending operations
      initializationPromiseRef.current = null;
      documentLoadPromiseRef.current = null;
      
      // Clean up documentUnloaded listener if it exists
      if (documentUnloadedCleanupRef.current) {
        try {
          documentUnloadedCleanupRef.current();
          documentUnloadedCleanupRef.current = null;
        } catch (error) {
          console.warn('⚠️ Error cleaning up documentUnloaded listener:', error);
        }
      }
      
      // IMMEDIATELY clear viewer DOM to stop any rendering that might trigger requests
      if (viewerElement) {
        viewerElement.innerHTML = '';
      }
      
      if (instanceRef.current) {
        const viewerInstance = instanceRef.current;
        instanceRef.current = null; // Clear ref immediately to prevent reuse
        
        // Properly dispose WebViewer using async pattern
        (async () => {
          await cleanupWebViewerInstance(viewerInstance, '🧹 Unmount cleanup: ');
        })();
      } else {
        console.log('⚠️ No instance found in unmount cleanup (may still be initializing)');
      }
    };
  }, []); // Empty deps - only runs on mount/unmount


  // OCR pages for the open document, fetched once and reused across searches.
  const loadOcrPages = (documentId) => {
    const cache = ocrPagesRef.current;
    if (cache.documentId === documentId) {
      if (cache.pages) return Promise.resolve(cache.pages);
      if (cache.promise) return cache.promise;
    }
    const promise = getDocumentMarkdown(documentId)
      .then((response) => {
        const markdown = response && response.status !== 404 ? response.data?.markdown_content : '';
        const pages = markdown ? splitMarkdownIntoPages(markdown) : [];
        ocrPagesRef.current = { documentId, pages, promise: null };
        return pages;
      })
      .catch((err) => {
        ocrPagesRef.current = { documentId: null, pages: null, promise: null };
        throw err;
      });
    ocrPagesRef.current = { documentId, pages: null, promise };
    return promise;
  };

  const goToOcrPage = (page) => {
    const documentViewer = instanceRef.current?.Core?.documentViewer;
    if (documentViewer && page > 0) {
      try { documentViewer.setCurrentPage(page); } catch (err) { console.warn('Could not jump to page', page, err); }
    }
  };

  const stepOcrPage = (delta) => {
    if (!ocrSearchFallback) return;
    const { pages, index } = ocrSearchFallback;
    const next = (index + delta + pages.length) % pages.length;
    goToOcrPage(pages[next]);
    setOcrSearchFallback({ ...ocrSearchFallback, index: next });
  };

  const setupDocumentListeners = (instance, pathForThisLoad, fileType) => {
    const { documentViewer, annotationManager } = instance.Core;

    // ✅ Clear rendering caches when document is unloaded to help with memory
    // This helps release canvas/rendering memory, though WASM memory still requires instance disposal
    const onDocumentUnloaded = () => {
      console.log('🧹 Document unloaded - clearing rendered page caches/canvases');
      try {
        // Clears rendered page caches/canvases to help release memory
        documentViewer.refreshAll();
      } catch (error) {
        console.warn('⚠️ Error calling refreshAll() on documentUnloaded:', error);
      }
    };

    documentViewer.addEventListener('documentUnloaded', onDocumentUnloaded);
    
    // Store cleanup function for later removal
    documentUnloadedCleanupRef.current = () => {
      try {
        documentViewer.removeEventListener('documentUnloaded', onDocumentUnloaded);
        console.log('🧹 Removed documentUnloaded listener');
      } catch (error) {
        console.warn('⚠️ Error removing documentUnloaded listener:', error);
      }
    };

    // Search fallback for scanned pages. Apryse reports every completed
    // search here with its results; an empty result on a PDF is answered
    // from the document's OCR text (fetched once per document).
    if (fileType === 'pdf' && instance.UI && typeof instance.UI.addSearchListener === 'function') {
      if (ocrSearchListenerRef.current && typeof instance.UI.removeSearchListener === 'function') {
        try { instance.UI.removeSearchListener(ocrSearchListenerRef.current); } catch (_) { /* ignore */ }
      }
      const onSearchCompleted = (searchValue, options, results) => {
        const query = String(searchValue || '').trim();
        if (!query || (results && results.length > 0)) {
          setOcrSearchFallback(null);
          return;
        }
        const documentId = String(documentRecord?.unique_id || '').substring(1);
        if (!documentId) return;
        highlightHiddenText(instance, documentId, query, { wholeWord: !!options?.wholeWord })
          .then((highlighted) => {
            if (!isMountedRef.current || highlighted) {
              if (highlighted) setOcrSearchFallback(null);
              return null;
            }
            return loadOcrPages(documentId);
          })
          .then((pages) => {
            if (!pages || !isMountedRef.current || !pages.length) return;
            const hits = findPagesForQuery(pages, query, { wholeWord: !!options?.wholeWord });
            setOcrSearchFallback(hits.length ? { documentId, query, pages: hits, index: 0 } : null);
          })
          .catch((err) => console.warn('OCR search fallback failed:', err));
      };
      ocrSearchListenerRef.current = onSearchCompleted;
      instance.UI.addSearchListener(onSearchCompleted);
    }

    // ✅ Listen to page changes for both PDF and MS Office files
    documentViewer.addEventListener("pageNumberUpdated", (newPageNumber) => {
      if (newPageNumber && newPageNumber > 0) {
        console.log("Current page changed:", newPageNumber);
        localStorage.setItem("lastPageViewed",newPageNumber)
        localStorage.setItem("lastPageViewedDoc",documentDetail.unique_id.substring(1))
      }
    });

    // ✅ Only enable editing listeners for PDFs (MS files are read-only)
    if (fileType === 'pdf') {
      annotationManager.addEventListener("annotationChanged", async (annotations, action, options) => {
        // Skip if this is an imported annotation (from remote update) or if we're applying a remote update
        if (options?.imported || isApplyingRemoteUpdate.current) return;

        if (["add", "modify", "delete"].includes(action)) {
          // Mark that user has made actual changes
          hasUserChangesRef.current = true;
          handleAutoSave(documentViewer, annotationManager, true);

          // Send annotation change to WebSocket for real-time sync
          try {
            const documentId = documentDetail?.id || documentRecord.unique_id.substring(1);
            const userId = Number(localStorage.getItem("user_id"));
            const xfdfCommand = await annotationManager.exportAnnotationCommand();
            
            const messagePayload = {
              type: 'annotation_change',
              document_id: documentId,
              xfdf_command: xfdfCommand,
              user_id: userId,
            };
            
            // Log the exact structure being sent for debugging
            console.log("[WS] Sending annotation change with payload:", JSON.stringify(messagePayload, null, 2));
            console.log("[WS] Payload structure:", {
              type: messagePayload.type,
              document_id: messagePayload.document_id,
              user_id: messagePayload.user_id,
              has_xfdf: !!messagePayload.xfdf_command,
              xfdf_length: messagePayload.xfdf_command?.length || 0,
            });
            
            dispatch(sendWebSocketMessage(messagePayload));
            console.log("[WS] Annotation change sent for document:", documentId);
          } catch (error) {
            console.error("[WS] Failed to send annotation change:", error);
          }
        }
      });

      // Removed pagesUpdated autosave - this triggers during document load/tab switch
      // Only trigger autosave on actual user changes

      annotationManager.addEventListener("fieldChanged", () => {
        // Mark that user has made actual changes
        hasUserChangesRef.current = true;
        setDocumentChanged(true, documentViewer, annotationManager);
      });

      // Only listen to keydown events for actual text input changes
      // Removed 'click' and 'mousemove' as they trigger even when just viewing
      const handleEvent = debounce((event) => {
        // Double-check file type (autosave is PDF-only)
        if (fileType !== 'pdf') {
          return;
        }

        // Only trigger on keydown events in editable fields (form fields, text annotations, etc.)
        if (
          event.type === "keydown" &&
          !DISABLE_AUTOSAVE_PLACEHOLDERS.includes(event.target.placeholder)
        ) {
          // Check if the event is in an editable context (form field, text annotation, etc.)
          const target = event.target;
          const isEditable = target.isContentEditable || 
                           target.tagName === 'INPUT' || 
                           target.tagName === 'TEXTAREA' ||
                           (target.closest && target.closest('[contenteditable="true"]'));
          
          if (isEditable) {
            hasUserChangesRef.current = true;
          firstLoad = true;
          handleAutoSave(documentViewer, annotationManager, true);
          }
        }
      }, 2000);

      // Only listen to keydown events - removed 'click' and 'mousemove'
      document.addEventListener('keydown', handleEvent);
    } else {
      console.log("📌 MS Office file - editing disabled, view-only mode");
    }

    // ✅ Common listeners for both file types
    documentViewer.addEventListener("documentLoaded", () => {
      if (fileType === 'pdf') {
        setDocumentChanged(false, documentViewer, annotationManager);

        // Cache the loaded PDF blob into IndexedDB for future tab switches / refreshes.
        // IMPORTANT: Wait for the full document download to complete before exporting.
        // With streaming enabled, `documentLoaded` fires after the first page is ready,
        // NOT after the entire file has been downloaded. Calling getFileData() too early
        // would produce a truncated/incomplete PDF.
        (async () => {
          try {
            const doc = documentViewer.getDocument();
            if (doc) {
              // Wait until every byte of the streamed document has arrived
              if (typeof doc.getDocumentCompletePromise === 'function') {
                await doc.getDocumentCompletePromise();
              }
              if (!isMountedRef.current) return; // component may have unmounted while waiting
              const docId = documentRecord.unique_id.substring(1);
              const data = await doc.getFileData({ xfdfString: await annotationManager.exportAnnotations(), doctype: 'pdf' });
              const blob = new Blob([data], { type: 'application/pdf' });
              const version = documentDetail?.updated_at || documentDetail?.version;
              if (version) {
                await cacheDocument(docId, blob, version);
                console.log('📦 PDF cached in IndexedDB (full download verified)');
              }
            }
          } catch (err) {
            console.warn('Failed to cache PDF after load:', err);
          }
        })();
      }
      setisLoading(false);
      setDocLoading(false);
    firstLoad = true;
    // Reset change flag on document load - no autosave should trigger on load/tab switch
    hasUserChangesRef.current = false;

      console.log(`✅ ${fileType.toUpperCase()} document loaded event fired`);
    console.log('📍 Location state:', location.state);
    console.log('📍 Location pathname:', location.pathname);
    console.log('📍 Highlight metadata ref:', highlightMetadataRef.current);
    
    // Also check sessionStorage as fallback
    let sessionStorageMetadata = null;
    try {
      const stored = sessionStorage.getItem('chatHighlightMetadata');
      if (stored) {
        sessionStorageMetadata = JSON.parse(stored);
        console.log('📍 Highlight metadata from sessionStorage:', sessionStorageMetadata);
      }
    } catch (e) {
      console.error('Failed to read from sessionStorage:', e);
    }

    // Handle text highlighting from chat navigation
    // Use ref first, then location.state, then sessionStorage
    const highlightMetadata = highlightMetadataRef.current || location.state?.highlightMetadata || sessionStorageMetadata;
    console.log('📍 Using highlight metadata:', highlightMetadata);
    
    if (highlightMetadata && Array.isArray(highlightMetadata) && highlightMetadata.length > 0 && fileType === 'pdf') {
      const { Core } = instance;
      const { documentViewer, annotationManager } = Core;
      
      highlightMetadata.forEach((metadata) => {
        const { page, chunk_content } = metadata;
        if (!chunk_content || !page) return;

        // Extract a searchable portion of the text (first 100 chars for better matching)
        const searchText = chunk_content.substring(0, 100).trim();
        if (!searchText) return;

        // Wait for the specific page to be loaded
        const waitForPage = () => {
            const totalPages = documentViewer.getPageCount();
          if (page > totalPages) {
            // Page not loaded yet, wait a bit
            setTimeout(waitForPage, 100);
            return;
          }

          // Navigate to the page first
          documentViewer.setCurrentPage(page);

          // Use PDFTron's text search to find and highlight the text
              setTimeout(() => {
            try {
              // Search for the text on the specific page
              const searchOptions = {
                caseSensitive: false,
                wholeWord: false,
                wildcard: false,
                regex: false,
              };

              // Use text search API
              documentViewer.textSearchInit(searchText, searchOptions);
              const handleSearchResults = (results) => {
                if (results && results.length > 0) {
                  // Find results on the target page
                  const pageResults = results.filter(r => r.PageNum === page);
                  if (pageResults.length > 0) {
                    // Create highlight annotations for each match
                    pageResults.forEach((result) => {
                      const highlight = new Core.Annotations.TextHighlightAnnotation();
                      highlight.PageNumber = result.PageNum;
                      highlight.Quads = result.Quads;
                      highlight.StrokeColor = new Core.Annotations.Color(255, 255, 0, 0.3); // Yellow highlight
                      highlight.setContents(chunk_content);
                      
                      annotationManager.addAnnotation(highlight);
                    });
                    
                    annotationManager.drawAnnotationsFromList([pageResults[0].PageNum]);
                    console.log(`✅ Highlighted text on page ${page}`);
                  }
                }
                documentViewer.removeEventListener('textSearchResults', handleSearchResults);
              };

              documentViewer.addEventListener('textSearchResults', handleSearchResults);
              // Trigger the search
              documentViewer.textSearchNext();
        } catch (error) {
              console.error('Error highlighting text:', error);
            }
          }, 500); // Small delay to ensure page is rendered
        };

        waitForPage();
      });
    }

    if (!documentDetail?.last_page_viewed) return;
    setLoadingStatus("loading")
    const lastPage = Number.parseInt(documentDetail.last_page_viewed, 10);
    if (!lastPage || lastPage <= 0) return;

    let navigated = false;

    // MS Office files load pages lazily
    const tryNavigate = () => {
      // totalPages may still be 1 at first
      const totalPages = documentViewer.getPageCount();
      
      // Only navigate if lastPage has been loaded/rendered
      if (!navigated && lastPage <= totalPages) {
        navigated = true;
        setLoadingStatus("idle")
        documentViewer.setCurrentPage(lastPage);
        console.log(`📄 Navigated to last viewed page: ${lastPage}`);

        // Cleanup listener
        documentViewer.removeEventListener('pageComplete', tryNavigate);
        }
    };

    if (PDF_FILE_TYPES.includes(fileType)   ) {
      // PDFs can navigate immediately
      documentViewer.setCurrentPage(lastPage);
    } else {
      // MS Office → wait until page is rendered
      documentViewer.addEventListener('pageComplete', tryNavigate);
      tryNavigate(); // also try immediately
      }
    });

    documentViewer.addEventListener("contentLoaded", () => {
      setisLoading(false);
      setDocLoading(false);
    });

    documentViewer.addEventListener('workerError', (err) => {
      console.error(`${fileType.toUpperCase()} Worker Error:`, err);
    });

    instance.UI.addEventListener(instance.UI.Events.LOAD_ERROR, (err) => {
      if (pathForThisLoad !== activePathRef.current) {
        console.warn("Ignored stale loaderror for:", pathForThisLoad);
        return;
      }
      const id = Date.now().toString();
      instance.UI.disableElements(['progressModal']);

      let newMsg = "Failed to open the file";

      if (err?.detail?.message === "Network failure") {
        newMsg = "Network error - failed to load file";
      } else if (err.type === "loaderror") {
        newMsg = fileType === 'office' 
          ? "The file is not a valid Office document" 
          : "The file is not a valid PDF document";
      }
      setShowNotification(true);
      setErrorMsg((prev) => [...prev, { id, newMsg }]);
      setisLoading(false);
      setDocLoading(false);
    });

    // DON'T set isLoading to false here - keep loader visible until documentLoaded/contentLoaded events fire
    // This prevents navigation during document loading
  };

  useEffect(() => {
    configureDocumentPermissions(documentDetail?.locked_by === Number(localStorage.getItem("user_id")), fileType)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentDetail.is_locked]); // configureDocumentPermissions is stable and does not need to be in deps

  // Handle incoming annotation updates from WebSocket
  useEffect(() => {
    if (!annotationUpdate || !instanceRef.current || fileType !== 'pdf') {
      return;
    }

    const currentDocumentId = documentDetail?.id || documentRecord.unique_id.substring(1);
    
    // Only apply updates for the current document
    if (String(annotationUpdate.document_id) !== String(currentDocumentId)) {
      return;
    }

    // Don't apply updates from the current user (they already made the change)
    const currentUserId = Number(localStorage.getItem("user_id"));
    if (annotationUpdate.user_id === currentUserId) {
      dispatch(clearAnnotationUpdate());
      return;
    }

    // Invalidate IndexedDB cache — the blob is now stale due to a remote edit
    invalidateDocument(String(currentDocumentId))
      .catch(err => console.warn('Failed to invalidate cache on remote update:', err));

    const { annotationManager } = instanceRef.current.Core;
    
    try {
      console.log("[WS] Applying remote annotation update:", annotationUpdate);
      
      // Set flag to prevent sending this update back to server
      isApplyingRemoteUpdate.current = true;
      
      // Import and draw the annotation from XFDF command
      annotationManager.importAnnotationCommand(annotationUpdate.xfdf_command)
        .then((annotations) => {
          annotationManager.drawAnnotationsFromList(annotations);
          console.log("[WS] Remote annotations applied successfully");
          isApplyingRemoteUpdate.current = false;
        })
        .catch((error) => {
          console.error("[WS] Failed to apply remote annotations:", error);
          isApplyingRemoteUpdate.current = false;
        });
      
      // Clear the update after processing
      dispatch(clearAnnotationUpdate());
    } catch (error) {
      console.error("[WS] Error processing annotation update:", error);
      isApplyingRemoteUpdate.current = false;
      dispatch(clearAnnotationUpdate());
    }
  }, [annotationUpdate, documentDetail?.id, documentRecord.unique_id, fileType, dispatch]);

  useEffect(() => {
    if (!treeData || !documentRecord?.unique_id) return;

    const tabExpandedKeys = getExpandedKeys(treeData, documentRecord.unique_id);

    dispatch(setExpandedKeys([...prevExpandedKeys, ...tabExpandedKeys]));
  }, [treeData, documentRecord]);

  useEffect(() => {
    dispatch(fetchBreadCrumbs({ item_id: urlIds.at(-1) })).then(
      (response) => {
        setDocumentBreadcrumb([
          { id: 0, name: "Matters" },
          ...(response?.payload?.data || []),
        ]);
      }
    );
  }, []);

  const configureDocumentPermissions = (isLocked, fileType) => {
    if (!instanceRef.current) return;

    const currentUserId = Number(localStorage.getItem("user_id"));
    const lockedByUserId = documentDetail.locked_by;

    const result = configurePermissions(
      instanceRef.current,
      isLocked,
      fileType,
      currentUserId,
      lockedByUserId
    );

    console.log('🔧 Permissions configured:', result);
  };

  const handleDocumentBreadcrumbClick = (folderId, index) => {
    if (index + 1 !== documentBreadcrumb?.length) {
      handleBreadcrumbClick(folderId, index, documentBreadcrumb);
      setActiveTab("1");
    }
  };

  const handleMSEditorClick = () => {
    if(documentDetail?.sharepoint_web_url)
    window.open(absolutizeSharePointUrl(documentDetail?.sharepoint_web_url), "_blank", "noopener,noreferrer");
  }

  let bannerContent = null;





  if (
    documentDetail?.is_locked === true &&
    documentDetail?.locked_by !== Number(localStorage.getItem("user_id"))
  ) {
    bannerContent = (
      <WarningLabel>
        File is being edited by{" "}
        <SpanName>{documentDetail?.locked_by_user || documentDetail?.locked_by_name}</SpanName>. Only{" "}
        <SpanBold>view mode</SpanBold> is enabled
      </WarningLabel>
    );
  } else if (autoSave) {
    bannerContent = <WarningLabel>{autoSave}</WarningLabel>;
  }
  return (
    <div 
      style={{ paddingTop: '3px', height: "100%" }}
      >
      {isLoading && <Loader />}
      {showActions && (
        <DocumentContentHeaderWrapper>
          <div>
            <Breadcrumb
              breadcrumb={documentBreadcrumb}
              onBreadcrumbClick={handleDocumentBreadcrumbClick}
            />
          </div>
          <div>
            {(documentDetail.is_locked ? bannerContent : null) || (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {MS_FILE_TYPES.includes(documentDetail.file_type) && (
                  
                 (loadingStatus==="loading")?
                 (<ViewMSWrapper>Returning you to the page you last viewed. Please wait...</ViewMSWrapper>):( <ViewMSWrapper>
                    File Opened in View Mode.
                    <ViewMSFileButton onClick={handleMSEditorClick}>
                      Edit in MS Editor
                    </ViewMSFileButton>
                  </ViewMSWrapper>)
                )}
                <AnalyzeButton documentId={documentIdForAI} />
                <Button
                  variant="contained"
                  data-testid="create-work-item-button"
                  onClick={() => setWorkItemModalOpen(true)}
                  disabled={!documentIdForAI}
                  sx={
                    MS_FILE_TYPES.includes(documentDetail.file_type)
                      ? secondaryButtonStyles
                      : buttonStyles
                  }
                >
                  Create Work Item
                </Button>
                <MoreButton
                  buttonText="Share"
                  variant="secondary"
                  top={"40px"}
                  customwidth={"unset"}
                  menuOptions={[
                    {
                      label: "External Share",
                      icon: ExternalShare,
                      onClick: () => {
                        setShowExternalShareModal(true);
                        setSelectedDoc(documentDetail);
                      },
                    },
                    {
                      label: "Send Message",
                      icon: SendIcon,
                      onClick: () => {
                        setSelectedDoc(documentDetail);
                        setShowSendMessageModal?.(true);
                      },
                    },
                  ]}
                />
                {showActionButton && (
                  <MoreButton
                    customwidth={"unset"}
                    buttonText="Actions"
                    variant="secondary"
                    top={"40px"}
                    menuOptions={[
                      {
                        label: "Approve",
                        icon: Approve,
                        onClick: () => {
                          setSelectedDoc(documentDetail);
                          setShowReviewActionModal("approve");
                        },
                      },
                      {
                        label: "Approve & forward to…",
                        icon: Approve,
                        onClick: () => {
                          setSelectedDoc(documentDetail);
                          setShowReviewActionModal("approve_forward");
                        },
                      },
                      {
                        label: "Request Rework",
                        icon: Rework,
                        onClick: () => {
                          setSelectedDoc(documentDetail);
                          setShowReviewActionModal("rework");
                        },
                      },
                    ]}
                    disabled={actionDisabled}
                  />
                )}
                {showSubmitButton && (
                  <Button
                    variant="contained"
                    disabled={submitDisabled}
                    onClick={() => {
                      setSelectedDoc(documentDetail);
                      setShowReviewActionModal("submit");
                    }}
                    sx={buttonStyles}
                  >
                    Submit
                  </Button>
                )}
                <MoreButton
                  top={"40px"}
                  customwidth={"unset"}
                  menuOptions={[
                    {
                      label: isAnalysisRunningForDoc
                        ? "AI Analysis in progress…"
                        : "Run AI Analysis",
                      icon: AnalysisIcon,
                      disabled: isAnalysisRunningForDoc || !documentIdForAI,
                      onClick: openAIAnalysisTab,
                    },
                    {
                      label: "Download",
                      icon: DownloadIcon,
                      onClick: async () => {
                        try {
                          await downloadDocumentFile(documentRecord);
                        } catch (err) {
                          console.error("Failed to download document", err);
                          errorHandler(
                            "Failed to download file.",
                            setErrorMsg,
                            setShowNotification
                          );
                        }
                      },
                    },
                    {
                      label: "Rename",
                      icon: Rename,
                      onClick: () => {
                        setRenameEditFlag((prev) => ({
                          ...prev,
                          [documentRecord.unique_id]: true,
                        }));
                      },
                    },
                    {
                      label: "Delete",
                      icon: Delete,
                      onClick: () => {
                        setShowDeleteModal(true);
                        setSelectedDoc(documentRecord);
                      },
                    },
                     PDF_FILE_TYPES.includes(documentRecord?.file_type) &&{
                      label: "Merge Files",
                      icon: Merge,
                      onClick: () => {
                        setSelectedFile(documentRecord)
                        setIsModalOpen(true)
                      },
                    },
                    {
                      label: "File Properties",
                      icon: FileProperties,
                      onClick: () => {
                        setFileProp(documentRecord);
                        setSelectedDoc(documentRecord);
                        dispatch(setUpdateMeta(documentRecord));
                        setOpen(true);
                      },
                    },
                    documentRecord?.is_review_notes_available && {
                      label: "Review Notes",
                      icon: ReviewNotes,
                      onClick: () => {
                        setSelectedDoc(documentRecord);
                        setOpenReviewNotes(true);
                      },
                    },
                    {
                      label: "Send for E-Signature",
                      icon: ESignImage,
                      onClick: () => {
                        setSelectedDoc(documentRecord);
                        setOpenESignModal(true);
                      },
                    },
                  ].filter(Boolean)}
                />
              </div>
            )}
          </div>
        </DocumentContentHeaderWrapper>
      )} 
      <div
        className="MyComponent"
        style={{
          padding: "20px",
          border: "1px solid #ddd",
          height: "100%",
          position: "relative",
        }}
      >
          {ocrSearchFallback && ocrSearchFallback.documentId === String(documentRecord?.unique_id || '').substring(1) && (
            <OcrSearchFallbackBanner
              query={ocrSearchFallback.query}
              pages={ocrSearchFallback.pages}
              index={ocrSearchFallback.index}
              onGoTo={goToOcrPage}
              onStep={stepOcrPage}
              onDismiss={() => setOcrSearchFallback(null)}
            />
          )}
          <div
            className="webviewer"
            ref={viewer}
            style={{ height: "100%" }}
          ></div>
      </div>
      {showNotification && (
        <ErrorToast
          message={errorMsg}
          onClose={(id) => {
            setErrorMsg((prev) => prev.filter((msg) => msg.id !== id))
          }
          } // Hide notification when closed
        />
      )}
      <CreateWorkItemModal
        open={workItemModalOpen}
        documentId={documentIdForAI}
        onClose={() => setWorkItemModalOpen(false)}
      />
    </div>
  );
};

export default Document;

Document.propTypes = {
  documentRecord: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    file_type: PropTypes.string,
    s3_path: PropTypes.string,
    s3_path_streaming: PropTypes.string,
    unique_id: PropTypes.string,
    is_locked: PropTypes.bool,
    locked_by: PropTypes.number,
    locked_by_user: PropTypes.string,
    locked_by_name: PropTypes.string,
    sharepoint_resource_url: PropTypes.string,
    category: PropTypes.string,
    is_review_notes_available:PropTypes.bool,
    last_page_viewed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  isLocked: PropTypes.bool,
  setRenameEditFlag: PropTypes.func.isRequired,
  setFileProp: PropTypes.func,
  setOpen: PropTypes.func,
  loading: PropTypes.bool,
  handleBreadcrumbClick: PropTypes.func,
  setActiveTab: PropTypes.func,
  rename: PropTypes.bool,
  setShowDeleteModal: PropTypes.func,
  setShowShareModal: PropTypes.func,
  setSelectedDoc: PropTypes.func,
  setShowReviewActionModal: PropTypes.func,
  setOpenReviewNotes: PropTypes.func,
  setShareId: PropTypes.func,
  setOpenESignModal: PropTypes.func.isRequired,
  openESignModal: PropTypes.bool,
  setShowExternalShareModal: PropTypes.func,
  setShowSendMessageModal: PropTypes.func,
  setDocLoading: PropTypes.func,
  setSelectedFile: PropTypes.func,
  setIsModalOpen: PropTypes.func,
};